import throttle from "lodash.throttle";

import EncoderAPI from "../api/EncoderAPI";
import OfferAPI from "../api/OfferAPI";

export const PART_SIZE = 5242880; // 5 MB
export const SIMULTANEOUS_UPLOAD_LIMIT = 30;

export interface UploadEngineOptions {
  mediaId: string;
  onProgress: (progress: number) => void;
  signal: AbortSignal;
}

export interface UploadEngineResult {
  uploadedFilename: string;
}

interface PartProgress {
  progress: number;
}

interface PartResult {
  ETag: string;
  PartNumber: number;
}

function abortError(): DOMException {
  return new DOMException("Aborted", "AbortError");
}

async function uploadPart(
  blob: Blob,
  url: string,
  partIdx: number,
  partList: PartProgress[],
  onProgress: (progress: number) => void,
  signal: AbortSignal,
): Promise<PartResult> {
  return new Promise<PartResult>((resolve, reject) => {
    if (signal.aborted) {
      reject(abortError());
      return;
    }

    const req = new XMLHttpRequest();
    req.open("PUT", url);

    const onAbort = (): void => req.abort();
    signal.addEventListener("abort", onAbort);
    const cleanup = (): void => signal.removeEventListener("abort", onAbort);

    req.addEventListener("load", () => {
      cleanup();
      if (req.status >= 200 && req.status <= 299) {
        resolve({
          ETag: req.getResponseHeader("ETag") ?? "",
          PartNumber: partIdx + 1,
        });
      } else {
        reject(new Error(`Part upload failed: ${req.status}`));
      }
    });
    req.addEventListener("error", () => {
      cleanup();
      reject(new Error("Part upload network error"));
    });
    req.addEventListener("abort", () => {
      cleanup();
      reject(abortError());
    });
    req.upload.addEventListener("progress", (event: ProgressEvent) => {
      if (event.total !== 0) {
        const part = partList[partIdx];
        if (part) {
          part.progress = (100 * event.loaded) / event.total;
          const sumProgress = partList.reduce(
            (acc, current) => acc + current.progress,
            0,
          );
          onProgress(Math.round(sumProgress / partList.length));
        }
      }
    });
    req.send(blob);
  });
}

async function completeUpload(
  completeUrl: string,
  etagList: PartResult[],
  signal: AbortSignal,
): Promise<void> {
  const partsStr = etagList
    .map(
      (etag) =>
        `<Part><PartNumber>${etag.PartNumber}</PartNumber><ETag>${etag.ETag}</ETag></Part>`,
    )
    .join("");
  const response = await fetch(completeUrl, {
    method: "POST",
    headers: { "Content-Type": "application/xml" },
    body: `<CompleteMultipartUpload>${partsStr}</CompleteMultipartUpload>`,
    signal,
  });
  if (!response.ok) {
    throw new Error(`Multipart completion failed: ${response.status}`);
  }
}

export async function runS3Upload(
  file: File,
  uploadedFilename: string,
  { mediaId, onProgress, signal }: UploadEngineOptions,
): Promise<UploadEngineResult> {
  if (signal.aborted) throw abortError();

  const throttledProgress = throttle(onProgress, 1000);
  const totalParts = Math.ceil(file.size / PART_SIZE);
  const { urls } = await EncoderAPI.createS3Upload({
    multipart: true,
    file_name: uploadedFilename,
    total_parts: totalParts,
    mimetype: file.type,
    media_uuid: mediaId,
  });

  const partList: PartProgress[] = urls.parts.map(() => ({ progress: 0 }));
  const chunkList = urls.parts.map((url, idx) => {
    const start = PART_SIZE * idx;
    const end = Math.min(start + PART_SIZE, file.size);
    const blob = file.slice(start, end);
    return async (): Promise<PartResult> =>
      uploadPart(blob, url, idx, partList, throttledProgress, signal);
  });

  const etagList: PartResult[] = [];
  for (let i = 0; i < chunkList.length; i += SIMULTANEOUS_UPLOAD_LIMIT) {
    if (signal.aborted) throw abortError();
    const group = chunkList.slice(i, i + SIMULTANEOUS_UPLOAD_LIMIT);
    // eslint-disable-next-line no-await-in-loop
    const resultList = await Promise.all(group.map(async (chunk) => chunk()));
    etagList.push(...resultList);
  }

  await completeUpload(urls.complete, etagList, signal);
  return { uploadedFilename };
}

export async function runVttUpload(
  file: File,
  { mediaId, signal }: UploadEngineOptions,
): Promise<UploadEngineResult> {
  if (signal.aborted) throw abortError();

  const startResponse = await EncoderAPI.uploadStart({
    media_name: file.name,
    media_uuid: mediaId,
  });

  if (signal.aborted) throw abortError();

  await EncoderAPI.uploadVtt({
    source_media_uuid: startResponse.source_media_uuid,
    clean_media_name: startResponse.clean_media_name,
    media_uuid: mediaId,
    file,
  });

  return { uploadedFilename: startResponse.clean_media_name };
}

export interface AdvertisingVideoUploadOptions {
  campaignUuid: string;
  languageUuid: string;
  projectSlug: string;
  onProgress: (progress: number) => void;
  signal: AbortSignal;
}

export async function runAdvertisingVideoUpload(
  file: File,
  {
    campaignUuid,
    languageUuid,
    projectSlug,
    onProgress,
    signal,
  }: AdvertisingVideoUploadOptions,
): Promise<UploadEngineResult> {
  if (signal.aborted) throw abortError();

  const throttledProgress = throttle(onProgress, 1000);

  const { filename } = await OfferAPI.uploadAdvertisingFile({
    uuid: campaignUuid,
    file,
    languageUuid,
    projectSlug,
    signal,
    onUploadProgress: (progressEvent: ProgressEvent): void => {
      if (!progressEvent.total) return;

      throttledProgress(
        Math.round((progressEvent.loaded * 100) / progressEvent.total),
      );
    },
  });

  return { uploadedFilename: filename };
}
